import { useState, useEffect, SyntheticEvent } from 'react';
import { Clickable, ClickableVariant } from '../Clickable';
import { Input, Password } from '../Input';
import { signIn, setIdle } from '../../actions';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { getStatusOfSignUp } from '../../selectors';
import { isPending, isRejected } from '../../utils';
import { Form } from './Form';
import { completeAccountSchema } from '../../utils/validationSchemas';
import { Api } from '../../utils/api';
import * as Yup from 'yup';

const COMPLETE_ACCOUNT_MAX_RETRIES = 3;
const COMPLETE_ACCOUNT_RETRY_DELAY_MS = 2000;

/**
 * [2026-05-01] Completes the account registration process with a retry mechanism.
 * If the account is not found (likely due to data propagation delay),
 * it triggers the `onRetry` callback and waits before the next attempt.
 */
async function completeAccountWithRetry(
  params: Parameters<typeof Api.completeAccount>[0],
  onRetry: () => void,
): Promise<void> {
  for (let attempt = 0; attempt <= COMPLETE_ACCOUNT_MAX_RETRIES; attempt++) {
    try {
      await Api.completeAccount(params);
      return;
    } catch (err: any) {
      const isNotFound = (err?.response?.data?.reason as string)?.includes(
        'No pre-registered account',
      );
      if (isNotFound && attempt < COMPLETE_ACCOUNT_MAX_RETRIES) {
        onRetry();
        await new Promise(resolve =>
          setTimeout(resolve, COMPLETE_ACCOUNT_RETRY_DELAY_MS),
        );
        continue;
      }
      throw err;
    }
  }
}

// [2026-04-25] Form to complete a pre-registered account after a Stripe payment.
// Collects name and password; email is pre-filled and read-only.
export function CompleteAccountForm({ email }: { email: string }) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    setIdle(dispatch, 'signUp');
  }, [dispatch]);

  const status = useAppSelector(getStatusOfSignUp);
  const isMakingRequest = isPending(status);
  const hasError = isRejected(status);

  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    password: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const handleChange = (field: string) => (value: string) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const onSubmit = async (evt: SyntheticEvent) => {
    evt.preventDefault();
    try {
      await completeAccountSchema.validate(formValues, { abortEarly: false });
      setErrors({});
      setFormError('');
      setIsSubmitting(true);
      setIsRetrying(false);

      await completeAccountWithRetry(
        {
          email,
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          password: formValues.password,
        },
        () => setIsRetrying(true),
      );

      await signIn(dispatch, email, formValues.password);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const fieldErrors: { [key: string]: string } = {};
        err.inner.forEach(error => {
          if (error.path) fieldErrors[error.path] = error.message;
        });
        setErrors(fieldErrors);
        setFormError('Please correct the highlighted fields to continue.');
      } else {
        console.error('Unexpected error:', err);
        setFormError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
      setIsRetrying(false);
    }
  };

  return (
    <Form className="flex flex-col gap-1" autoComplete="on" onSubmit={onSubmit}>
      <Input
        label="Email"
        name="email"
        value={email}
        isDisabled={true}
        isRequired
      />
      <div className="flex gap-1">
        <Input
          label="First Name"
          name="firstName"
          value={formValues.firstName}
          onChange={e => handleChange('firstName')(e.target.value)}
          error={errors.firstName}
          isDisabled={isMakingRequest}
          isRequired
        />
        <Input
          label="Last Name"
          name="lastName"
          value={formValues.lastName}
          onChange={e => handleChange('lastName')(e.target.value)}
          error={errors.lastName}
          isDisabled={isMakingRequest}
          isRequired
        />
      </div>
      <Password
        label="Password"
        name="password"
        value={formValues.password}
        onChange={e => handleChange('password')(e.target.value)}
        error={errors.password}
        isDisabled={isMakingRequest}
        isRequired
      />
      {formError && (
        <p className="color-red small mt1 text-center">{formError}</p>
      )}
      {hasError && (
        <p className="color-red small mt1 text-center">
          Sorry, we were unable to complete your account setup. Please try
          again.
        </p>
      )}
      <div className="flex justify-center">
        <Clickable
          isSubmit
          isDisabled={isSubmitting || isMakingRequest}
          label={
            isRetrying
              ? 'VERIFYING PAYMENT...'
              : isSubmitting || isMakingRequest
              ? 'SETTING UP YOUR ACCOUNT...'
              : 'COMPLETE SETUP'
          }
          variant={ClickableVariant.BUTTON_PRIMARY}
        />
      </div>
    </Form>
  );
}
